import {
  BrainCategory,
  BrainEntityType,
  BrainKnowledgeState,
  BrainPredicate,
} from './brain';
import {
  BrainSignalActions,
  BrainSignalLevel,
  BrainSignalMatch,
  BrainSignalNotifyChannel,
  BrainSignalNotifyRole,
  BrainSignalPolicy,
  BrainSignalRule,
  BrainSignalRuleKind,
  BrainSignalScope,
  BrainSignalScopeType,
  BrainSignalSemanticDepth,
} from './brainSignals';

export enum BrainSignalTemplateId {
  DEADLINE_SLIPPING = 'deadline-slipping',
  RECURRING_BLOCKER = 'recurring-blocker',
  OWNER_CHANGED = 'owner-changed',
  STUCK_DEPENDENCY = 'stuck-dependency',
  CUSTOMER_CHURN_RISK = 'customer-churn-risk',
  STAKEHOLDER_CHANGE = 'stakeholder-change',
  OPPORTUNITY_STALLED = 'opportunity-stalled',
  PRIORITY_SHIFT = 'priority-shift',
  PENDING_DECISION = 'pending-decision',
  INCIDENT_OPEN = 'incident-open',
}

export enum BrainSignalTemplateArea {
  SALES = 'SALES',
  PRODUCT = 'PRODUCT',
  ENGINEERING = 'ENGINEERING',
  GENERAL = 'GENERAL',
}

export interface BrainSignalTemplateSemantic {
  /** `brain.signals.templates.<templateId>.condition` */
  conditionKey: string;
  /** `brain.signals.templates.<templateId>.levels.<LEVEL>`, ascending. */
  levelKeys: { level: BrainSignalLevel; descriptionKey: string }[];
  depth: BrainSignalSemanticDepth;
}

export interface BrainSignalTemplate {
  templateId: BrainSignalTemplateId;
  area: BrainSignalTemplateArea;
  /** `brain.signals.templates.<templateId>.name` */
  nameKey: string;
  /** `brain.signals.templates.<templateId>.description` */
  descriptionKey: string;
  scope: BrainSignalScope;
  match: BrainSignalMatch;
  rules: BrainSignalRule[];
  policy: BrainSignalPolicy;
  actions: BrainSignalActions;
  semantic: BrainSignalTemplateSemantic;
}

const TEMPLATE_I18N_PREFIX = 'brain.signals.templates';
const DEFAULT_WINDOW_DAYS = 14;
const MONTH_DAYS = 30;
const STALE_TAIL_DAYS = 45;
const STALLED_SILENCE_DAYS = 21;
const INCIDENT_WINDOW_DAYS = 7;

export const brainSignalTemplateNameKey = (id: BrainSignalTemplateId): string =>
  `${TEMPLATE_I18N_PREFIX}.${id}.name`;

export const brainSignalTemplateDescriptionKey = (
  id: BrainSignalTemplateId,
): string => `${TEMPLATE_I18N_PREFIX}.${id}.description`;

export const brainSignalTemplateConditionKey = (
  id: BrainSignalTemplateId,
): string => `${TEMPLATE_I18N_PREFIX}.${id}.condition`;

export const brainSignalTemplateLevelKey = (
  id: BrainSignalTemplateId,
  level: BrainSignalLevel,
): string => `${TEMPLATE_I18N_PREFIX}.${id}.levels.${level}`;

const TEMPLATE_SEMANTIC_LEVELS: readonly BrainSignalLevel[] = [
  BrainSignalLevel.NONE,
  BrainSignalLevel.LOW,
  BrainSignalLevel.MODERATE,
  BrainSignalLevel.HIGH,
  BrainSignalLevel.CRITICAL,
];

const templateSemantic = (
  id: BrainSignalTemplateId,
  depth: BrainSignalSemanticDepth,
): BrainSignalTemplateSemantic => ({
  conditionKey: brainSignalTemplateConditionKey(id),
  levelKeys: TEMPLATE_SEMANTIC_LEVELS.map((level) => ({
    level,
    descriptionKey: brainSignalTemplateLevelKey(id, level),
  })),
  depth,
});

const entityScope = (entityTypes: BrainEntityType[]): BrainSignalScope => ({
  type: BrainSignalScopeType.ENTITY,
  entityTypes,
});

const defaultPolicy = (reviewBeforeAction: boolean): BrainSignalPolicy => ({
  cooldownHours: 24,
  decayDays: DEFAULT_WINDOW_DAYS,
  closeOnResolution: true,
  reviewBeforeAction,
  maxEvaluationsPerDay: 200,
});

const defaultActions = (): BrainSignalActions => ({
  notify: {
    userIds: [],
    roles: [BrainSignalNotifyRole.ADMINS],
    channels: [BrainSignalNotifyChannel.IN_APP],
    minLevel: BrainSignalLevel.MODERATE,
  },
});

const decayRule = (days: number): BrainSignalRule => ({
  kind: BrainSignalRuleKind.NO_NEW_EVIDENCE_FOR,
  days,
  delta: -1,
});

interface TemplateOptions {
  area: BrainSignalTemplateArea;
  depth?: BrainSignalSemanticDepth;
  reviewBeforeAction?: boolean;
}

const buildTemplate = (
  templateId: BrainSignalTemplateId,
  {
    area,
    depth = BrainSignalSemanticDepth.LIGHT,
    reviewBeforeAction = false,
  }: TemplateOptions,
  parts: Pick<BrainSignalTemplate, 'scope' | 'match' | 'rules'>,
): BrainSignalTemplate => ({
  templateId,
  area,
  nameKey: brainSignalTemplateNameKey(templateId),
  descriptionKey: brainSignalTemplateDescriptionKey(templateId),
  semantic: templateSemantic(templateId, depth),
  ...parts,
  policy: defaultPolicy(reviewBeforeAction),
  actions: defaultActions(),
});

export const BRAIN_SIGNAL_TEMPLATES: BrainSignalTemplate[] = [
  buildTemplate(
    BrainSignalTemplateId.DEADLINE_SLIPPING,
    { area: BrainSignalTemplateArea.GENERAL },
    {
      scope: entityScope([BrainEntityType.PROJECT, BrainEntityType.CUSTOMER]),
      match: {
        categories: [BrainCategory.DEADLINE],
        entityTypes: [BrainEntityType.PROJECT, BrainEntityType.CUSTOMER],
      },
      rules: [
        {
          kind: BrainSignalRuleKind.DEADLINE_MOVED,
          min: 2,
          days: MONTH_DAYS,
          delta: 1,
        },
        {
          kind: BrainSignalRuleKind.DEADLINE_PASSED_WITHOUT_SUCCESSOR,
          delta: 1,
        },
        decayRule(DEFAULT_WINDOW_DAYS),
      ],
    },
  ),
  buildTemplate(
    BrainSignalTemplateId.RECURRING_BLOCKER,
    { area: BrainSignalTemplateArea.PRODUCT },
    {
      scope: entityScope([BrainEntityType.PROJECT, BrainEntityType.SYSTEM]),
      match: {
        categories: [BrainCategory.BLOCKER],
        entityTypes: [BrainEntityType.PROJECT, BrainEntityType.SYSTEM],
      },
      rules: [
        {
          kind: BrainSignalRuleKind.COUNT_IN_WINDOW,
          category: BrainCategory.BLOCKER,
          min: 2,
          days: DEFAULT_WINDOW_DAYS,
          delta: 1,
        },
        {
          kind: BrainSignalRuleKind.COUNT_IN_WINDOW,
          category: BrainCategory.BLOCKER,
          min: 3,
          days: DEFAULT_WINDOW_DAYS,
          delta: 1,
        },
        decayRule(DEFAULT_WINDOW_DAYS),
      ],
    },
  ),
  buildTemplate(
    BrainSignalTemplateId.OWNER_CHANGED,
    { area: BrainSignalTemplateArea.GENERAL },
    {
      scope: entityScope([BrainEntityType.PROJECT, BrainEntityType.CUSTOMER]),
      match: {
        categories: [BrainCategory.OWNER],
        entityTypes: [BrainEntityType.PROJECT, BrainEntityType.CUSTOMER],
      },
      rules: [
        {
          kind: BrainSignalRuleKind.OWNER_CHANGED,
          days: MONTH_DAYS,
          delta: 1,
        },
        decayRule(MONTH_DAYS),
      ],
    },
  ),
  // The +1 and the decay are the same rule kind on purpose: silence for 14 days adds a point,
  // silence for 45 days takes it back, because a dependency nobody mentions anymore is stale, not stuck.
  buildTemplate(
    BrainSignalTemplateId.STUCK_DEPENDENCY,
    { area: BrainSignalTemplateArea.ENGINEERING },
    {
      scope: entityScope([BrainEntityType.PROJECT]),
      match: {
        categories: [BrainCategory.BLOCKER],
        predicates: [BrainPredicate.BLOCKED_BY],
        entityTypes: [BrainEntityType.PROJECT],
      },
      rules: [
        {
          kind: BrainSignalRuleKind.FACT_STATE_IS,
          knowledgeState: BrainKnowledgeState.ACTIVE,
          category: BrainCategory.BLOCKER,
          predicate: BrainPredicate.BLOCKED_BY,
          delta: 1,
        },
        {
          kind: BrainSignalRuleKind.NO_NEW_EVIDENCE_FOR,
          days: DEFAULT_WINDOW_DAYS,
          delta: 1,
        },
        decayRule(STALE_TAIL_DAYS),
      ],
    },
  ),
  buildTemplate(
    BrainSignalTemplateId.CUSTOMER_CHURN_RISK,
    {
      area: BrainSignalTemplateArea.SALES,
      depth: BrainSignalSemanticDepth.DEEP,
      reviewBeforeAction: true,
    },
    {
      scope: entityScope([BrainEntityType.CUSTOMER]),
      match: {
        categories: [
          BrainCategory.DECISION,
          BrainCategory.BLOCKER,
          BrainCategory.DEADLINE,
        ],
        entityTypes: [BrainEntityType.CUSTOMER],
      },
      rules: [
        {
          kind: BrainSignalRuleKind.NO_NEW_EVIDENCE_FOR,
          days: MONTH_DAYS,
          delta: 1,
        },
        {
          kind: BrainSignalRuleKind.SUPERSEDED_COUNT,
          min: 2,
          days: MONTH_DAYS,
          delta: 1,
        },
        decayRule(STALE_TAIL_DAYS * 2),
      ],
    },
  ),
  buildTemplate(
    BrainSignalTemplateId.STAKEHOLDER_CHANGE,
    { area: BrainSignalTemplateArea.SALES, reviewBeforeAction: true },
    {
      scope: entityScope([BrainEntityType.CUSTOMER]),
      match: {
        categories: [BrainCategory.OWNER],
        entityTypes: [BrainEntityType.CUSTOMER],
      },
      rules: [
        {
          kind: BrainSignalRuleKind.OWNER_CHANGED,
          days: MONTH_DAYS,
          delta: 1,
        },
        decayRule(MONTH_DAYS),
      ],
    },
  ),
  buildTemplate(
    BrainSignalTemplateId.OPPORTUNITY_STALLED,
    { area: BrainSignalTemplateArea.SALES },
    {
      scope: entityScope([BrainEntityType.CUSTOMER, BrainEntityType.PROJECT]),
      match: {
        categories: [BrainCategory.DEADLINE, BrainCategory.DECISION],
        entityTypes: [BrainEntityType.CUSTOMER, BrainEntityType.PROJECT],
      },
      rules: [
        {
          kind: BrainSignalRuleKind.NO_NEW_EVIDENCE_FOR,
          days: STALLED_SILENCE_DAYS,
          delta: 1,
        },
        {
          kind: BrainSignalRuleKind.DEADLINE_PASSED_WITHOUT_SUCCESSOR,
          delta: 1,
        },
        decayRule(STALE_TAIL_DAYS * 2),
      ],
    },
  ),
  buildTemplate(
    BrainSignalTemplateId.PRIORITY_SHIFT,
    { area: BrainSignalTemplateArea.PRODUCT },
    {
      scope: entityScope([BrainEntityType.PROJECT]),
      match: {
        categories: [BrainCategory.DECISION],
        entityTypes: [BrainEntityType.PROJECT],
      },
      rules: [
        {
          kind: BrainSignalRuleKind.SUPERSEDED_COUNT,
          predicate: BrainPredicate.DECIDED_TO,
          min: 2,
          days: MONTH_DAYS,
          delta: 1,
        },
        {
          kind: BrainSignalRuleKind.SUPERSEDED_COUNT,
          predicate: BrainPredicate.DECIDED_TO,
          min: 3,
          days: MONTH_DAYS,
          delta: 1,
        },
        decayRule(MONTH_DAYS),
      ],
    },
  ),
  buildTemplate(
    BrainSignalTemplateId.PENDING_DECISION,
    {
      area: BrainSignalTemplateArea.PRODUCT,
      depth: BrainSignalSemanticDepth.DEEP,
    },
    {
      scope: entityScope([BrainEntityType.PROJECT]),
      match: {
        categories: [BrainCategory.DECISION, BrainCategory.DEADLINE],
        entityTypes: [BrainEntityType.PROJECT],
      },
      rules: [
        {
          kind: BrainSignalRuleKind.DEADLINE_PASSED_WITHOUT_SUCCESSOR,
          delta: 1,
        },
        {
          kind: BrainSignalRuleKind.FACT_STATE_IS,
          knowledgeState: BrainKnowledgeState.ACTIVE,
          category: BrainCategory.DECISION,
          predicate: BrainPredicate.DECIDED_TO,
          delta: 1,
        },
        decayRule(STALE_TAIL_DAYS),
      ],
    },
  ),
  buildTemplate(
    BrainSignalTemplateId.INCIDENT_OPEN,
    { area: BrainSignalTemplateArea.ENGINEERING },
    {
      scope: entityScope([BrainEntityType.SYSTEM]),
      match: {
        categories: [BrainCategory.BLOCKER],
        entityTypes: [BrainEntityType.SYSTEM],
      },
      rules: [
        {
          kind: BrainSignalRuleKind.COUNT_IN_WINDOW,
          category: BrainCategory.BLOCKER,
          min: 2,
          days: INCIDENT_WINDOW_DAYS,
          delta: 1,
        },
        {
          kind: BrainSignalRuleKind.COUNT_IN_WINDOW,
          category: BrainCategory.BLOCKER,
          min: 4,
          days: INCIDENT_WINDOW_DAYS,
          delta: 1,
        },
        decayRule(DEFAULT_WINDOW_DAYS),
      ],
    },
  ),
];

export const findBrainSignalTemplate = (
  templateId: string,
): BrainSignalTemplate | undefined =>
  BRAIN_SIGNAL_TEMPLATES.find((template) => template.templateId === templateId);
